import { useCallback, useEffect, useState } from 'react';
import { DUGZI_APPS, Link, MenuButton, appHref, useToast, type MenuEntry } from '@dugzi/ui';
import { ChevronRight, Clock, Download, FileText, FolderPlus, FolderUp, HardDrive, Plus, Presentation, Sheet, Star, Trash2, Upload, Users } from 'lucide-react';
import { api } from '../lib/api';
import { bytes } from '../lib/format';
import type { Me } from '../lib/types';
import type { View } from './Browser';
import { DRAG_TYPE, dropMove } from './dnd';

interface Props {
  me: Me;
  view: View;
  treeKey: number;
  canCreate: boolean;
  onUpload(): void;
  onUploadFolder(): void;
  onNewFolder(): void;
  onMoved(): void;
  onSettings(): void;
  onImport(): void;
}

export function Sidebar({ me, view, treeKey, canCreate, onUpload, onUploadFolder, onNewFolder, onMoved, onSettings, onImport }: Props) {
  const create = (id: string) => {
    const app = DUGZI_APPS.find((a) => a.id === id)!;
    window.open(appHref(app, me.accountsUrl, me.user.id, '/?new=1'), '_blank', 'noopener');
  };
  const items: MenuEntry[] = [
    { label: 'New folder', icon: <FolderPlus aria-hidden />, shortcut: '⇧F', onSelect: onNewFolder, disabled: !canCreate, testId: 'menu-new-folder' },
    { separator: true },
    { label: 'Upload files', icon: <Upload aria-hidden />, shortcut: 'U', onSelect: onUpload, testId: 'menu-upload' },
    { label: 'Upload a folder', icon: <FolderUp aria-hidden />, shortcut: '⇧U', onSelect: onUploadFolder, testId: 'menu-upload-folder' },
    { label: 'Import from Google or OneDrive…', icon: <Download aria-hidden />, hint: 'Copy once, or a zip', onSelect: onImport, testId: 'menu-import' },
    { separator: true },
    { label: 'New document', icon: <FileText aria-hidden />, hint: 'Docs', onSelect: () => create('docs') },
    { label: 'New spreadsheet', icon: <Sheet aria-hidden />, hint: 'Sheets', onSelect: () => create('sheets') },
    { label: 'New presentation', icon: <Presentation aria-hidden />, hint: 'Slides', onSelect: () => create('slides') },
  ];
  const here = (k: View['kind'], id?: string) => view.kind === k && (!id || (view.kind === 'folder' && view.id === id));
  const pct = Math.min(100, (me.usage.usedBytes / Math.max(1, me.usage.quotaBytes)) * 100);
  return (
    <nav className="side" aria-label="Drive">
      <MenuButton items={items} label="New" variant="primary" icon={<Plus aria-hidden size={18} />} className="dz-btn dz-btn--primary side__new" testId="new-menu" menuLabel="New">New</MenuButton>
      <ul className="side__nav">
        <li>
          <TreeRoot me={me} active={here('folder', 'root')} current={view.kind === 'folder' ? view.id : null} treeKey={treeKey} onMoved={onMoved} />
        </li>
        <li><Link to="/recent" className="side__link" aria-current={here('recent') ? 'page' : undefined} data-testid="nav-recent"><Clock aria-hidden size={18} />Recent</Link></li>
        <li><Link to="/starred" className="side__link" aria-current={here('starred') ? 'page' : undefined} data-testid="nav-starred"><Star aria-hidden size={18} />Starred</Link></li>
        <li><Link to="/shared" className="side__link" aria-current={here('shared') ? 'page' : undefined} data-testid="nav-shared"><Users aria-hidden size={18} />Shared with me</Link></li>
        <li><Link to="/trash" className="side__link" aria-current={here('trash') ? 'page' : undefined} data-testid="nav-trash"><Trash2 aria-hidden size={18} />Trash</Link></li>
      </ul>
      <button type="button" className="meter" onClick={onSettings} data-testid="storage-meter" aria-label={`Storage: ${bytes(me.usage.usedBytes)} of ${bytes(me.usage.quotaBytes)} used. Open Drive settings`}>
        <span className="meter__bar" aria-hidden="true"><span style={{ width: `${pct}%` }} className={pct > 90 ? 'is-full' : ''} /></span>
        <span className="meter__text">{bytes(me.usage.usedBytes)} of {bytes(me.usage.quotaBytes)} used</span>
      </button>
    </nav>
  );
}

interface TreeItem { id: string; name: string; hasChildren: boolean }

function useChildren(parent: string, open: boolean, key: number) {
  const [kids, setKids] = useState<TreeItem[] | null>(null);
  useEffect(() => {
    if (!open) return;
    let live = true;
    api<{ data: TreeItem[] }>(`/api/tree?parent=${parent}`).then((r) => { if (live) setKids(r.data); }).catch(() => { if (live) setKids([]); });
    return () => { live = false; };
  }, [parent, open, key]);
  return kids;
}

/** My Drive and its folders, expanding on demand. Every folder is a drop target for moving. */
function TreeRoot({ me, active, current, treeKey, onMoved }: { me: Me; active: boolean; current: string | null; treeKey: number; onMoved(): void }) {
  const [open, setOpen] = useState(true);
  const kids = useChildren('root', open, treeKey);
  return (
    <div className="tree">
      <div className="tree__row">
        <button type="button" className="tree__twist" aria-label={open ? 'Collapse My Drive' : 'Expand My Drive'} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <ChevronRight aria-hidden size={16} className={open ? 'is-open' : ''} />
        </button>
        <DropLink to="/" id={me.rootId} active={active} onMoved={onMoved} testId="nav-mydrive"><HardDrive aria-hidden size={18} />My Drive</DropLink>
      </div>
      {open && kids && kids.length > 0 && (
        <ul className="tree__kids" data-testid="folder-tree">
          {kids.map((k) => <TreeNode key={k.id} item={k} depth={1} current={current} treeKey={treeKey} onMoved={onMoved} />)}
        </ul>
      )}
    </div>
  );
}

function TreeNode({ item, depth, current, treeKey, onMoved }: { item: TreeItem; depth: number; current: string | null; treeKey: number; onMoved(): void }) {
  const [open, setOpen] = useState(false);
  const kids = useChildren(item.id, open, treeKey);
  return (
    <li>
      <div className="tree__row" style={{ paddingLeft: depth * 12 }}>
        {item.hasChildren
          ? <button type="button" className="tree__twist" aria-label={`${open ? 'Collapse' : 'Expand'} ${item.name}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}><ChevronRight aria-hidden size={16} className={open ? 'is-open' : ''} /></button>
          : <span className="tree__twist" aria-hidden="true" />}
        <DropLink to={`/folders/${item.id}`} id={item.id} active={current === item.id} onMoved={onMoved}>{item.name}</DropLink>
      </div>
      {open && kids && kids.length > 0 && (
        <ul className="tree__kids">{kids.map((k) => <TreeNode key={k.id} item={k} depth={depth + 1} current={current} treeKey={treeKey} onMoved={onMoved} />)}</ul>
      )}
    </li>
  );
}

function DropLink({ to, id, active, onMoved, children, testId }: { to: string; id: string; active: boolean; onMoved(): void; children: React.ReactNode; testId?: string }) {
  const toast = useToast();
  const [over, setOver] = useState(false);
  const drop = useCallback(async (e: React.DragEvent) => {
    setOver(false);
    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    if (await dropMove(e.dataTransfer, id, toast)) onMoved();
  }, [id, onMoved, toast]);
  return (
    <Link to={to} className={`side__link tree__link${over ? ' is-over' : ''}`} aria-current={active ? 'page' : undefined} data-testid={testId} data-folder-id={id}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(true); } }}
      onDragLeave={() => setOver(false)} onDrop={drop}>
      {children}
    </Link>
  );
}
